import { Authenticator, Flex } from "@aws-amplify/ui-react"
import { Theme as MuiTheme, useMediaQuery } from "@mui/material"
import { Amplify } from "aws-amplify"
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito"
import { CookieStorage } from "aws-amplify/utils"
import { ReactElement, useMemo } from "react"
import { useSearchParams } from "react-router-dom"

import { authFormFields } from "./authFormFields"
import { authServices } from "./authService"
import awsExports from "./aws-exports"
import { mfaGuide } from "./mfaGuide"
import LoginScreenEvcp from "../assets/login-screen-evcp.png"
import LoginScreenHp from "../assets/login-screen-hp.png"
import ConnectDirectLogo from "../components/logos/logos"

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore: Unreachable code error
// eslint-disable-next-line no-undef
const CONFIG = GLOBAL_CONFIG

// Configure amplify authenticator with app cognito user-pool. Password
// policy must be kept aligned with cognito.
Amplify.configure({
    Auth: {
        Cognito: {
            userPoolId: awsExports.USER_POOL_ID,
            userPoolClientId: awsExports.USER_POOL_APP_CLIENT_ID,
            passwordFormat: {
                minLength: 10,
                requireLowercase: true,
                requireNumbers: true,
                requireSpecialCharacters: true,
                requireUppercase: true,
            },
        },
    },
})

cognitoUserPoolsTokenProvider.setKeyValueStorage(
    new CookieStorage({
        domain: CONFIG.WEB_APPLICATION_URL,
        sameSite: "strict",
        secure: CONFIG.SECURE,
    })
)

/**
 * Provides AWS cognito sign-in functionality. A user must authentication
 * successfully to access child components. If a user is not authenticated
 * and tries to access children, they will be redirected to a sign-in page.
 * This page allows user to create an account, sign into an account and
 * reset a forgotten password. The sign-up form is defined in the following
 * typescript files: `authFormFields.ts`

 */

interface AuthProviderProps {
    initialState?: "signIn" | "signUp" | "forgotPassword"
}

const AuthProvider = ({ initialState }: AuthProviderProps): ReactElement => {
    const [searchParams] = useSearchParams()
    const emailFromUrl = searchParams.get("email") || ""

    const formFields = useMemo(
        () =>
            emailFromUrl
                ? {
                      ...authFormFields,
                      forgotPassword: {
                          ...authFormFields.forgotPassword,
                          username: {
                              ...authFormFields.forgotPassword.username,
                              defaultValue: emailFromUrl,
                          },
                      },
                  }
                : authFormFields,
        [emailFromUrl]
    )

    const isMob = useMediaQuery((th: MuiTheme) => th.breakpoints.down("md"))

    return (
        <Flex
            style={{
                backgroundImage: `url("${
                    Math.round(Math.random()) === 1
                        ? LoginScreenEvcp
                        : LoginScreenHp
                }")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                backgroundSize: "cover",
            }}
            direction={isMob ? "column" : "row"}
            justifyContent="space-between"
            alignContent="flex-start"
            width="100vw"
            height="100vh"
            padding={16}
        >
            {!isMob && (
                <Flex alignItems="flex-start">
                    <ConnectDirectLogo
                        fontSize="350px"
                        viewBox="440 350 1070 400"
                        modeOverride="dark"
                    />
                </Flex>
            )}
            <Authenticator
                initialState={initialState}
                className="authContainer"
                components={mfaGuide({
                    onShowMfaHelp: () => undefined,
                })}
                formFields={formFields}
                services={authServices}
            />
        </Flex>
    )
}
AuthProvider.defaultProps = {
    initialState: "signIn",
}
export default AuthProvider
